import { useMemo } from 'react';
import { intersection } from 'lodash-es';
import type { MenuDataProps } from '@/components/AntdForm/antd-components.def.ts';
import { type ModuleFunction, Permission } from '@/features/auth/auth.def.ts';
import { useAuth } from '@/hooks/useAuth.ts';
import { navMenuData } from '@/router/routes.tsx';

/** Top-level items a user without portal access still sees (developer tools outside the portal). */
export const DEVELOPER_TOOL_NAV_KEYS: ReadonlySet<string> = new Set(['dataMaskingRuns']);

export type NavVisibilityContext = {
  userRoles: string[];
  hasPortalAccess: boolean;
  checkPermissions: (required: ModuleFunction[]) => boolean;
};

/** The item's own gates: any allowed role, and every required permission. */
export const isNavItemVisible = (
  item: Pick<MenuDataProps, 'allowRoles' | 'requiredPermissions'>,
  { userRoles, checkPermissions }: Pick<NavVisibilityContext, 'userRoles' | 'checkPermissions'>,
): boolean => {
  if (item.allowRoles?.length && !intersection(item.allowRoles, userRoles).length) {
    return false;
  }

  return !item.requiredPermissions?.length || checkPermissions(item.requiredPermissions);
};

const filterItems = (items: MenuDataProps[], context: NavVisibilityContext): MenuDataProps[] =>
  items.flatMap((item) => {
    if (!isNavItemVisible(item, context)) return [];
    if (!item.children?.length) return [item];

    const children = filterItems(item.children, context);
    // A section whose children are all hidden is dropped unless it is a page itself.
    if (!children.length && !item.url) return [];

    return [{ ...item, children: children.length ? children : undefined }];
  });

/**
 * The nav tree this user may open: users without portal access see only the developer tools;
 * hidden items are removed and empty sections dropped. Returns new objects, never mutates `data`.
 */
export const filterVisibleNavMenu = (
  data: MenuDataProps[],
  context: NavVisibilityContext,
): MenuDataProps[] =>
  filterItems(
    context.hasPortalAccess ? data : data.filter((item) => DEVELOPER_TOOL_NAV_KEYS.has(item.key)),
    context,
  );

/** `navMenuData` filtered for the signed-in user (nav menu and home page portal guide). */
const permissionKey = ({ moduleName, functionName }: ModuleFunction) =>
  `${moduleName}:${functionName}`;

export const useVisibleNavMenuData = (): MenuDataProps[] => {
  // `permissions` and `roles` come from the auth slice, so they are stable between renders;
  // useAuth().checkHasAllPermissions is a new function each render and would defeat the memo.
  const { user, permissions } = useAuth();
  const roles = user?.roles;

  return useMemo(() => {
    const granted = new Set((permissions ?? []).map(permissionKey));
    const checkPermissions = (required: ModuleFunction[]) =>
      required.every((permission) => granted.has(permissionKey(permission)));

    return filterVisibleNavMenu(navMenuData, {
      userRoles: roles ?? [],
      hasPortalAccess: checkPermissions([Permission.PortalAccess]),
      checkPermissions,
    });
  }, [permissions, roles]);
};
